import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import { useParams, useNavigate } from 'react-router-dom';

import Nav from './nav';
import { BASEURL, PRIMARYCOLOR, COMPANY } from '../../Constants';
import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';
import WhiteLabelForm from '../../components/whiteLabelForm';

import './ReportsPage.css';

window.html2canvas = html2canvas;

const PAGE_W = 816;
const PAGE_H = 1056;
const DEFAULT_PRIMARY = PRIMARYCOLOR || '#005CC5';
const DEFAULT_COMPANY = COMPANY || 'StealthNet AI';
const DEFAULT_CLIENT = '';

const FieldCard = ({ label, value, primary }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 3,
      border: `1px solid ${alpha('#0f172a', 0.08)}`,
      background: alpha(primary, 0.04),
      minHeight: 84,
    }}
  >
    <Typography
      sx={{
        mb: 0.45,
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: '#64748b',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: '0.98rem',
        fontWeight: 600,
        lineHeight: 1.45,
        color: '#0f172a',
      }}
    >
      {value || '-'}
    </Typography>
  </Box>
);

const SignatureBlock = ({ label, name, title }) => (
  <Box sx={{ minWidth: 0 }}>
    <Box
      sx={{
        height: 42,
        mb: 1,
        borderBottom: '1px solid #cbd5e1',
      }}
    />
    <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: '#0f172a' }}>
      {name}
    </Typography>
    <Typography sx={{ fontSize: '0.76rem', color: '#64748b', lineHeight: 1.45 }}>
      {title}
    </Typography>
    <Typography sx={{ mt: 0.4, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', color: '#94a3b8', textTransform: 'uppercase' }}>
      {label}
    </Typography>
  </Box>
);

const AttestationLetterDarkweb = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();
  const reportRef = useRef(null);

  const [pentests, setPentests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [primaryColors, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [companyName, setCompanyName] = useState(DEFAULT_COMPANY);
  const [clientName, setClientName] = useState(DEFAULT_CLIENT);

  const applyWhiteLabel = ({ primary, company, client }) => {
    if (primary !== undefined) setPrimaryColor(primary);
    if (company !== undefined) setCompanyName(company);
    if (client !== undefined) setClientName(client);
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    fetch(`${BASEURL}/pentests`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.error && data?.message === 'Invalid token') {
          navigate('/');
          return;
        }

        setPentests(data?.pentests || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate]);

  const current = pentests.find((item) => item.id === Number(pentest_id));

  const today = useMemo(() => new Date(), []);
  const dateStr = useMemo(
    () => today.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    [today]
  );

  const BRAND = primaryColors || '#0A68FF';
  const BRAND_DARK = darken(BRAND, 0.2);
  const BRAND_DEEP = darken(BRAND, 0.38);
  const BRAND_LIGHT = lighten(BRAND, 0.28);
  const ORG = companyName || 'StealthNet AI';
  const certificateId = `${ORG.replace(/\s+/g, '').toUpperCase()}-DARKWEB-${pentest_id}-${today.getFullYear()}`;
  const verificationUrl = `${window.location.origin}/verify/attestation/${pentest_id}?type=darkweb`;
  const issuedTo = clientName || current?.client_name || current?.company || 'Client Organization';
  const engagementName = current?.name || `Dark Web Monitoring #${pentest_id}`;

  const handleDownloadPDF = () => {
    html2pdf()
      .set({
        margin: 0,
        filename: `${ORG.replace(/\s+/g, '')}_Dark_Web_Certificate_${pentest_id}.pdf`,
        image: { type: 'jpeg', quality: 1 },
        html2canvas: { scale: 2, useCORS: true, allowTaint: false },
        jsPDF: { unit: 'px', format: [PAGE_W, PAGE_H], orientation: 'portrait' },
      })
      .from(reportRef.current)
      .save();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box className="no-print" sx={{ position: 'relative', pb: 2 }}>
        <Nav />
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          sx={{
            position: 'absolute',
            top: 8,
            right: 16,
            background: DEFAULT_PRIMARY,
            '&:hover': { background: darken(DEFAULT_PRIMARY, 0.12) },
          }}
        >
          Download Certificate
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card className="report-config-card" sx={{ width: '100%', maxWidth: '8.5in', mb: 2, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.08)' }}>
          <WhiteLabelForm
            defaultPrimary={DEFAULT_PRIMARY}
            defaultCompany={DEFAULT_COMPANY}
            defaultClient={DEFAULT_CLIENT}
            onApply={applyWhiteLabel}
            showCompany
            showClient
            title="White Label"
          />
        </Card>
      </Box>

      <Box className="report-platform-shell" sx={{ minHeight: '100vh', py: 2, px: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', overflowX: 'auto', pb: 3 }}>
          <Box
            sx={{
              width: PAGE_W,
              minWidth: PAGE_W,
              borderRadius: '30px',
              overflow: 'hidden',
              boxShadow: '0 40px 90px rgba(2, 6, 23, 0.16)',
            }}
          >
            <Box
              ref={reportRef}
              sx={{
                width: PAGE_W,
                height: PAGE_H,
                position: 'relative',
                overflow: 'hidden',
                background: '#ffffff',
                color: '#0f172a',
                fontFamily: '"Poppins", "Inter", sans-serif',
              }}
            >
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    `radial-gradient(420px 260px at 88% 10%, ${alpha(BRAND_LIGHT, 0.18)} 0%, transparent 68%),
                     radial-gradient(520px 320px at 10% 100%, ${alpha(BRAND, 0.08)} 0%, transparent 72%),
                     linear-gradient(180deg, #fdfefe 0%, #f7fafe 100%)`,
                }}
              />

              <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box
                  sx={{
                    position: 'relative',
                    px: 7,
                    pt: 6.5,
                    pb: 5.5,
                    background: `linear-gradient(145deg, ${BRAND_DEEP} 0%, #0c1524 46%, #122035 100%)`,
                    color: '#ffffff',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      top: -36,
                      right: -42,
                      width: 240,
                      height: 240,
                      borderRadius: '30px',
                      border: `1px solid ${alpha('#dbeafe', 0.08)}`,
                      background: `linear-gradient(180deg, ${alpha(BRAND_LIGHT, 0.18)} 0%, ${alpha(BRAND_DARK, 0.08)} 100%)`,
                      transform: 'rotate(10deg)',
                    }}
                  />

                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <ShieldOutlinedIcon sx={{ color: alpha('#ffffff', 0.9) }} />
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', color: alpha('#dbeafe', 0.92) }}>
                        {ORG}
                      </Typography>
                    </Stack>
                    <Chip
                      label="CONFIDENTIAL"
                      size="small"
                      sx={{
                        bgcolor: '#b00020',
                        color: '#fff',
                        fontWeight: 800,
                        letterSpacing: '0.14em',
                        borderRadius: '999px',
                        '& .MuiChip-label': { px: 1.35 },
                      }}
                    />
                  </Stack>

                  <Box sx={{ position: 'relative', zIndex: 1, mt: 7 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: alpha('#cbd5e1', 0.98), mb: 1.5 }}>
                      Certificate of Attestation
                    </Typography>
                    <Typography sx={{ fontFamily: '"Inter", "Poppins", sans-serif', fontSize: '2.9rem', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.03em', color: '#fff', maxWidth: 520, mb: 2.2 }}>
                      Dark Web Monitoring Attestation
                    </Typography>
                    <Box sx={{ width: 190, height: 4, borderRadius: 999, bgcolor: BRAND, mb: 2.5 }} />
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: alpha('#dbeafe', 0.94), maxWidth: 520 }}>
                      Independent confirmation that the monitored organization completed a third-party dark web monitoring review.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ px: 7, py: 4.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box
                    sx={{
                      p: 2.2,
                      borderRadius: 3.5,
                      border: `1px solid ${alpha('#0f172a', 0.08)}`,
                      background: alpha(BRAND, 0.04),
                    }}
                  >
                    <Typography sx={{ fontSize: '1rem', lineHeight: 1.7, color: '#334155' }}>
                      {ORG} attests that the organization identified below completed an independent dark web monitoring review designed to identify exposed identities, correlate validated breach intelligence, and document observed monitoring results for stakeholder review.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 1.5,
                    }}
                  >
                    <FieldCard label="Issued To" value={issuedTo} primary={BRAND} />
                    <FieldCard label="Engagement Name" value={engagementName} primary={BRAND} />
                    <FieldCard label="Assessment Type" value="Dark Web Monitoring" primary={BRAND} />
                    <FieldCard label="Date of Issue" value={dateStr} primary={BRAND} />
                    <FieldCard label="Monitoring Focus" value="Identity exposure, breach enrichment, and stakeholder reporting" primary={BRAND} />
                    <FieldCard label="Certificate ID" value={certificateId} primary={BRAND} />
                  </Box>

                  <Divider sx={{ borderColor: alpha('#0f172a', 0.08) }} />

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 0.8fr',
                      gap: 2.5,
                      alignItems: 'start',
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontFamily: '"Inter", "Poppins", sans-serif', fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', mb: 1 }}>
                        Attestation Statement
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.7, color: '#334155', mb: 1.2 }}>
                        The assessment team reviewed approved monitoring targets, evaluated exposed identities against collected leak data, and enriched the results with breach intelligence where available. A report was produced to summarize observed exposure, validated breach overlap, and the areas that merit stakeholder follow-up.
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.7, color: '#334155' }}>
                        This certificate confirms completion of the monitoring review only and does not imply the absence of future exposure, breach activity, or new data disclosures after the assessment window represented by the report.
                      </Typography>
                    </Box>

                    <Card
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${alpha('#0f172a', 0.08)}`,
                        boxShadow: 'none',
                        background: '#ffffff',
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        <Stack spacing={1.1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <WorkspacePremiumOutlinedIcon sx={{ color: BRAND_DARK }} />
                            <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>
                              Verification
                            </Typography>
                          </Stack>
                          <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.55, color: '#475569' }}>
                            This certificate was issued by {ORG} for the engagement listed in this attestation.
                          </Typography>
                          <Stack direction="row" spacing={0.8} alignItems="center">
                            <VerifiedOutlinedIcon sx={{ color: '#2563eb', fontSize: 18 }} />
                            <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a' }}>
                              Verified engagement record
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.8} alignItems="center">
                            <DescriptionOutlinedIcon sx={{ color: '#2563eb', fontSize: 18 }} />
                            <Typography sx={{ fontSize: '0.82rem', color: '#475569', overflowWrap: 'anywhere' }}>
                              {certificateId}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.8} alignItems="center">
                            <LaunchOutlinedIcon sx={{ color: '#2563eb', fontSize: 18 }} />
                            <Typography sx={{ fontSize: '0.82rem', color: '#475569', overflowWrap: 'anywhere' }}>
                              {verificationUrl}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    </Card>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 4,
                      mt: 1,
                    }}
                  >
                    <SignatureBlock label="Authorized By" name="Lead Assessor" title={`${ORG} | Security Operations`} />
                    <SignatureBlock label="Reviewed By" name="Quality Reviewer" title={`${ORG} | Independent Review`} />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AttestationLetterDarkweb;
